/**
 * @file Admin UI — FormService.
 */


import axiosClient from "../../../../api/axiosClient";
import { API_URLS } from "../../../../constants/apiUrls";
import type { FormField } from "../../../../interfaces/formInterfaces";

/** GET /forms/admin — optional ?materialId= hydrates material-scoped field options. */
export const getAllFormSections = async (options?: {
  materialId?: number;
}) => {
  const params =
    options?.materialId != null &&
    Number.isFinite(options.materialId) &&
    options.materialId > 0
      ? { materialId: options.materialId }
      : undefined;
  const response = await axiosClient.get(API_URLS.FORMS_ADMIN.ALL_STAGES, {
    params,
  });
  return response.data;
};

/** GET /forms/admin/field-types — enum + labels for field type dropdowns. */
export type FieldTypeOption = { value: string; label: string };

const FALLBACK_FIELD_TYPES: FieldTypeOption[] = [
  { value: "TEXT", label: "Text" },
  { value: "EMAIL", label: "Email" },
  { value: "READONLY", label: "Readonly" },
  { value: "RADIO", label: "Radio" },
  { value: "CHECKBOX", label: "Checkbox" },
  { value: "DROPDOWN", label: "Dropdown" },
  { value: "EXTRACTION_PROCESS", label: "Extraction process" },
  { value: "MANUFACTURING_PROCESS", label: "Manufacturing process" },
  { value: "PLANT_CAPACITY", label: "Plant capacity" },
  { value: "UNIT", label: "Unit" },
  { value: "CALENDAR", label: "Date picker" },
  { value: "MATERIALS", label: "Material selector" },
  { value: "SUBMATERIALS", label: "SubMaterial selector" },
  { value: "RAW_MATERIALS", label: "Raw material selector" },
  { value: "RAW_MATERIAL_QUALITY", label: "Raw material quality" },
  { value: "TECHNOLOGY_SPECIFICATION", label: "Technology specification" },
  { value: "MANUFACTURER_SPECIFICATION", label: "Manufacturer specification" },
  { value: "FILE", label: "File upload" },
  { value: "IMAGE", label: "Image upload" },
];

/** Exported const — get Field Types. */
export const getFieldTypes = async (): Promise<FieldTypeOption[]> => {
  try {
    const response = await axiosClient.get(API_URLS.FORMS_ADMIN.FIELD_TYPES);
    const body = response.data as { data?: unknown };
    const raw = body?.data;
    if (!Array.isArray(raw) || raw.length === 0) {
      return FALLBACK_FIELD_TYPES;
    }
    const mapped = (raw as Record<string, unknown>[])
      .map((row) => ({
        value: String(row.value ?? row.type ?? "").trim(),
        label: String(row.label ?? row.name ?? row.value ?? "").trim(),
      }))
      .filter((o) => o.value);
    return mapped.length ? mapped : FALLBACK_FIELD_TYPES;
  } catch {
    return FALLBACK_FIELD_TYPES;
  }
};

/** Options for TEXT fields (text, number, double, …) — GET /forms/admin/text-input-types */
export type TextInputTypeOption = { value: string; label: string };

const FALLBACK_TEXT_INPUT_TYPES: TextInputTypeOption[] = [
  { value: "", label: "Default (text)" },
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "double", label: "Double" },
];

/** Exported const — get Text Input Types. */
export const getTextInputTypes = async (): Promise<TextInputTypeOption[]> => {
  try {
    const response = await axiosClient.get(API_URLS.FORMS_ADMIN.TEXT_INPUT_TYPES);
    const body = response.data as { data?: unknown; success?: boolean };
    const raw = body?.data ?? (Array.isArray(body) ? body : null);
    if (!Array.isArray(raw) || raw.length === 0) {
      return FALLBACK_TEXT_INPUT_TYPES;
    }
    const first = raw[0];
    if (typeof first === "string") {
      const rest = (raw as string[])
        .filter((s) => s != null && String(s).trim() !== "")
        .map((s) => {
          const value = String(s).trim();
          return { value, label: humanizeInputType(value) };
        });
      return [{ value: "", label: "Default (text)" }, ...rest];
    }
    const mapped = (raw as Record<string, unknown>[])
      .map((row) => {
        const value = String(row.value ?? row.code ?? row.key ?? "").trim();
        const labelRaw = String(
          row.label ?? row.name ?? row.title ?? row.value ?? ""
        ).trim();
        const label = labelRaw || (value ? humanizeInputType(value) : "");
        return { value, label };
      })
      .filter((o) => o.label || o.value);
    const hasDefault = mapped.some((o) => o.value === "");
    return hasDefault
      ? mapped
      : [{ value: "", label: "Default (text)" }, ...mapped];
  } catch {
    return FALLBACK_TEXT_INPUT_TYPES;
  }
};

function humanizeInputType(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ");
}

/** One field with relations — GET /forms/admin/field/:fieldId */
export const getAdminFormFieldById = async (
  fieldId: number
): Promise<FormField | null> => {
  try {
    const response = await axiosClient.get(
      API_URLS.FORMS_ADMIN.FIELD_BY_ID(fieldId)
    );
    const body = response.data as {
      data?: FormField;
      field?: FormField;
      success?: boolean;
    };
    const data = body?.data ?? body?.field ?? (body as unknown as FormField);
    if (data && typeof (data as FormField).id === "number") {
      return data as FormField;
    }
    return null;
  } catch {
    return null;
  }
};

/** Bulk order — PATCH /forms/admin/fields/reorder */
export const reorderAdminFormFields = async (
  items: { id: number; order: number }[]
) => {
  const response = await axiosClient.patch(API_URLS.FORMS_ADMIN.FIELDS_REORDER, {
    items,
  });
  return response.data;
};

// 🔹 Add new form section
export const createFormSection = async (data: any) => {
  const response = await axiosClient.post("/forms/admin/section", data);
  return response.data;
};

// 🔹 Add new form subsection
export const createFormSubSection = async (data: any) => {
  const response = await axiosClient.post("/forms/admin/subsection", data);
  return response.data;
};

// 🔹 Add new form field — POST /forms/admin/field
export const createFormField = async (data: any) => {
  const response = await axiosClient.post(API_URLS.FORMS_ADMIN.FIELD_POST, data);
  return response.data;
};

// 🔹 Delete form field
export const deleteFormField = async (fieldId: number) => {
  const response = await axiosClient.delete(
    API_URLS.FORMS_ADMIN.FIELD_BY_ID(fieldId)
  );
  return response.data;
};

/** Exported const — update Form Field. */
export const updateFormField = async (fieldId: number, data: any) => {
  const response = await axiosClient.patch(
    API_URLS.FORMS_ADMIN.FIELD_BY_ID(fieldId),
    data
  );
  return response.data;
};
